import React, { useState, useEffect } from 'react';
import { getOpenFoodFactsImageUrl } from '../utils/foodImageUtils';
import { getLocalFoodImageUrl, hasLocalPhoto } from '../utils/photoUtils';
import './FoodImage.css';

/**
 * FoodImage component with database-specific image loading:
 * - Swiss/USDA foods: Only use local photos from food_catalog
 * - Open Food Facts foods: Only use OFF remote images via EAN
 * - Custom foods/recipes: Show placeholder (no images)
 */

interface FoodImageProps {
  ean?: string;
  foodId?: string;
  foodName: string;
  language: 'en' | 'de';
  size?: '100' | '200' | '400' | 'full';
  className?: string;
}

export const FoodImage: React.FC<FoodImageProps> = ({
  ean,
  foodId,
  foodName,
  language,
  size = '200',
  className = ''
}) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    // Reset state when props change
    setHasError(false);
    setImageUrl(null);
    
    const loadImage = async () => {
      setIsLoading(true);
      
      // Determine food database from foodId
      const isSwissFood = foodId?.startsWith('swiss_');
      const isUSDAFood = foodId?.startsWith('usda_');
      const isOFFFood = foodId?.startsWith('off_');
      
      // For Swiss and USDA foods, try local photos first
      if ((isSwissFood || isUSDAFood) && foodId && size !== 'full') {
        try {
          const hasLocal = await hasLocalPhoto(foodId);
          if (hasLocal) {
            const localUrl = getLocalFoodImageUrl(foodId, size);
            if (localUrl) {
              setImageUrl(localUrl);
              setIsLoading(false);
              return;
            }
          }
        } catch (error) {
          console.log('Local photo check failed:', error);
          // No fallback for Swiss/USDA foods - only show local photos
        }
      }
      
      // For Open Food Facts foods, use OFF images if online and has EAN
      if (isOFFFood && isOnline && ean && ean.match(/^\d+$/)) {
        const primaryUrl = getOpenFoodFactsImageUrl(ean, 'front', language, size);
        if (primaryUrl) {
          setImageUrl(primaryUrl);
          setIsLoading(false);
          return;
        }
      }
      
      setIsLoading(false);
    };

    loadImage();
  }, [ean, foodId, language, size, isOnline]);

  // Handle image loading errors
  const handleImageError = () => {
    setHasError(true);
    setImageUrl(null);
  };

  // Show placeholder if loading, error, or no image available
  if (isLoading || hasError || !imageUrl) {
    return (
      <div className={`food-image-placeholder ${className}`}>
        <div className="food-image-icon">
          {isLoading ? (
            <div className="loading-spinner" />
          ) : (
            <svg viewBox="0 0 24 24" className="placeholder-icon">
              <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
            </svg>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`food-image-container ${className}`}>
      <img
        src={imageUrl}
        alt={foodName}
        className="food-image"
        onError={handleImageError}
        loading="lazy"
      />
    </div>
  );
};